import {ActionMenuContext} from '#/components/actionmenu/action-menu';
import {ActionMenuInfoTile} from '#/components/actionmenu/action-menu-info-tile';
import {ActionMenuLayoutContentNamed, ActionMenuLayoutFooter} from '#/components/actionmenu/action-menu-layout';
import {actionMenuMessageContent, useMessageCasesToContent} from '#/components/actionmenu/action-menu-message-content';
import {TransactionVariables} from '#/components/actionmenu/sections/transaction/transaction-flow-data';
import {ActionMenuFormContainer, ActionMenuTransactionReviewButton} from '#/components/actionmenu/subcomponents';
import {MessageCase} from '#/components/actionmenu/types';
import {useCoinDisplayPreferences} from '#/hooks/use-coin-display-preferences';
import {useDismissableInfoTile} from '#/hooks/use-dismisable-info-tile';
import {DefaultChildren} from '#/utils/utils';
import {TransactionType} from '@shareland/shareland-common';
import React, {FunctionComponent, useContext} from 'react';
import {useTranslation} from 'react-i18next';

export const ActionMenuRentClaimTab: FunctionComponent<DefaultChildren> = () => {
  // Context
  const {landAccountInfo, initiateTransaction} = useContext(ActionMenuContext);

  // External hooks
  const {t} = useTranslation('translation');
  const displayPref = useCoinDisplayPreferences();
  const [showInfoTile, onCloseTile] = useDismissableInfoTile(TransactionType.ClaimRewards);

  const transactionVars: TransactionVariables[TransactionType.ClaimRewards] = {
    claimableStableAmount: landAccountInfo.claimableStableToken,
  };
  const noticeMessages = useNoticeMessages();
  const canStartTransactionFlow = noticeMessages.every(elem => elem.canSubmitForm);

  // Functions
  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!canStartTransactionFlow) return;

    initiateTransaction({
      type: TransactionType.ClaimRewards,
      data: transactionVars,
    });
  };

  return (
    <ActionMenuFormContainer onSubmit={onSubmit}>
      <ActionMenuLayoutContentNamed noticeMessages={noticeMessages}>
        {{
          info: showInfoTile && <ActionMenuInfoTile onClose={onCloseTile} tileKey={TransactionType.ClaimRewards} />,
          grow: (
            <div className='w-full h-full flex flex-col justify-center items-center gap-2'>
              <span className='text-white font-semibold text-3xl'>
                {t('pages.actionMenu.rent.sections.claim.claimableAmount.title')}
              </span>
              <span className='text-deepblue font-semibold text-3xl'>
                {t('pages.actionMenu.rent.sections.claim.claimableAmount.value', {
                  currencySymbol: displayPref.symbol,
                  amount: displayPref.displayAmount(transactionVars.claimableStableAmount),
                })}
              </span>
            </div>
          ),
        }}
      </ActionMenuLayoutContentNamed>

      <ActionMenuLayoutFooter>
        <ActionMenuTransactionReviewButton disabled={!canStartTransactionFlow} />
      </ActionMenuLayoutFooter>
    </ActionMenuFormContainer>
  );
};

const useNoticeMessages = () => {
  const {land, landAccountInfo} = useContext(ActionMenuContext);
  const {t} = useTranslation('translation');

  const cases: Record<string, MessageCase> = {
    nothingToClaim: {
      condition: () => landAccountInfo.lpTokenAmount === 0,
      message: () => actionMenuMessageContent.mustRentToCollectRewards(t, land.tokenSymbol),
    },
    noClaimaibleAmount: {
      condition: () => landAccountInfo.claimableStableToken === 0,
      message: () => actionMenuMessageContent.allRewardsAreClaimed(t),
    },
  };

  return useMessageCasesToContent(cases);
};
